import { RecurringState, Txn } from '../money.model';
import {
  RecurringSeries, Direction, recurringKey, collapseSameDay, dayOfMonthSigma,
  occurrencesPerMonth, calendarMonthsSpanned, hasNoLongGap,
  DOM_SIGMA_MAX, MAX_PER_MONTH, MIN_DOM_OCCURRENCES,
  addDays, CADENCE_DAYS, isSeriesStale, median,
  BILLING_DAY_DRIFT, merchantFromKey, ordinal,
} from '../rules/recurring.detector';
import { spendableTxns } from '../dashboard/dashboard.aggregate';
import { hash64 } from '../csv/parse-values';

/** Billing days are 1..31 by construction — see splitByBillingDay. */
const MAX_BILLING_DAY = 31;

/** True when the user has explicitly marked this key a bill by hand. */
export function isPinned(key: string, state: Record<string, RecurringState>): boolean {
  return state[key]?.status === 'pinned';
}

/**
 * Undecided, confirmed, and pinned series; dismissed ones always drop out,
 * and stale ones drop out too UNLESS the user has pinned them — a pinned
 * series has no cadence a detector would trust in the first place (that's
 * why it needed pinning), so staleness says nothing about it.
 */
export function visibleSeries(
  series: RecurringSeries[], state: Record<string, RecurringState>,
): RecurringSeries[] {
  return (series ?? []).filter(s =>
    state[s.key]?.status !== 'dismissed' && (!s.isStale || isPinned(s.key, state)));
}

/**
 * The fixed monthly nut: visible EXPENSE series only. Recurring income — the
 * paycheck is the largest recurring series in the real ledger — is reported
 * separately and never summed in here. Stale series drop out via
 * visibleSeries, so a series that has stopped occurring never contributes to
 * the nut forever.
 */
export function monthlyNut(
  series: RecurringSeries[], state: Record<string, RecurringState>,
): number {
  return visibleSeries(series, state)
    .filter(s => s.direction === 'expense')
    .reduce((n, s) => n + s.monthlyEquivalentCents, 0);
}

/**
 * Stale, non-dismissed, non-pinned series — surfaced separately so a series
 * that has gone quiet is shown as "no longer seen" rather than silently
 * vanishing. A pinned series is excluded here even when stale: it already
 * lives in visibleSeries as a fixed cost (see visibleSeries), and showing it
 * in both places would contradict the user's own decision to keep it.
 */
export function staleSeries(
  series: RecurringSeries[], state: Record<string, RecurringState>,
): RecurringSeries[] {
  return (series ?? []).filter(s =>
    state[s.key]?.status !== 'dismissed' && s.isStale && !isPinned(s.key, state));
}

/**
 * The merchant key a series belongs to, with any billing-day suffix removed.
 * `#` cannot appear in a normalized matchKey: `normalizeStem` strips it, and
 * `PRODUCT_TOKEN_PATTERN` is letters-only, so it is structurally impossible
 * to produce, not merely absent from today's data. So a `#` here can only
 * have come from splitByBillingDay.
 */
export function parentKey(key: string): string {
  const hash = key.indexOf('#');
  return hash < 0 ? key : key.slice(0, hash);
}

/** True when detection split this merchant into billing-day series. */
export function hasSplitChild(key: string, series: RecurringSeries[]): boolean {
  const prefix = `${key}#`;
  return (series ?? []).some(s => s.key.startsWith(prefix));
}

/**
 * Display name for any key, split or not. detectRecurring labels its own split
 * series, but the manual paths here derive a name from the key alone — and a
 * raw split key renders as "APPLE SERVICES#11", which is not a merchant name.
 */
export function merchantForKey(key: string): string {
  const parent = parentKey(key);
  if (parent === key) return merchantFromKey(key);
  return `${merchantFromKey(parent)} (${ordinal(Number(key.slice(parent.length + 1)))})`;
}

/**
 * Every non-transfer, non-excluded, non-zero row belonging to one key.
 *
 * A billing-day key (`expense:APPLE SERVICES#11`) matches its parent
 * merchant's rows narrowed to within `BILLING_DAY_DRIFT` days of the single
 * median day encoded in the key — otherwise every caller that maps a key
 * back to transactions (pinnedMonthlyCents, pinnedSeries, dismissedEntries)
 * silently sees nothing for a split series.
 *
 * This is NOT the same window `splitByBillingDay` clustered with. That
 * detector chains adjacent days together, so one cluster can legitimately
 * span more days than 2 * BILLING_DAY_DRIFT + 1 (e.g. days 10-13, median 12).
 * The key only carries the median day, not the original cluster's span, so
 * it cannot be reconstructed here: a row on day 10 of that cluster falls
 * outside the drift window around median 12 and is dropped. The practical
 * effect is an undercount on the manual paths that resolve a split key back
 * to rows (pinnedMonthlyCents, and dismissedEntries for a split key detection
 * no longer finds) whenever the original cluster spanned more than
 * 2 * BILLING_DAY_DRIFT + 1 days. Every row on an out-of-window day is
 * dropped, so the shortfall is one occurrence per such row, not one in total.
 */
function rowsForKey(txns: Txn[], key: string): Txn[] {
  const parent = parentKey(key);
  const day = parent === key ? null : Number(key.slice(parent.length + 1));

  return spendableTxns(txns ?? []).filter(t => {
    if (t.amount === 0) return false;
    const direction: Direction = t.amount < 0 ? 'expense' : 'income';
    if (recurringKey(t.matchKey, direction, t.description) !== parent) return false;
    if (day === null) return true;
    return Math.abs(Number(t.date.slice(8, 10)) - day) <= BILLING_DAY_DRIFT;
  });
}

/**
 * Monthly cost for a manually pinned series, costed the same way the
 * day-of-month detection path costs a variable series with no reliable
 * cadence: the sum of what was actually charged, spread across the full
 * calendar span rather than assumed to be exactly monthly. A pinned series
 * is always classified 'variable' (see pinnedSeries) — that's usually why
 * it needed pinning in the first place, a seasonal bill the median would
 * understate — so this always uses the true average, never the median.
 */
export function pinnedMonthlyCents(txns: Txn[], key: string): number {
  const collapsed = collapseSameDay(rowsForKey(txns, key));
  if (!collapsed.length) return 0;
  const months = calendarMonthsSpanned(collapsed.map(r => r.date));
  if (months === 0) return 0;
  const sumCents = collapsed.reduce((total, r) => total + Math.abs(r.amount), 0);
  return Math.round(sumCents / months);
}

/**
 * Synthesizes a full RecurringSeries for a merchant the user pinned by hand
 * that detection itself never found — that is the entire reason pinning
 * exists. Reuses the same grouping and cost math as pinnedMonthlyCents so the
 * two never disagree. Returns null if the key has no matching rows left
 * (e.g. the underlying transactions were re-imported away).
 */
export function pinnedSeries(txns: Txn[], key: string, asOf: string): RecurringSeries | null {
  const collapsed = collapseSameDay(rowsForKey(txns, key));
  if (!collapsed.length) return null;

  const amounts = collapsed.map(r => Math.abs(r.amount));
  const dates = collapsed.map(r => r.date);
  const lastDate = dates[dates.length - 1];

  return {
    key,
    merchant: merchantForKey(key),
    direction: key.startsWith('income:') ? 'income' : 'expense',
    cadence: 'monthly',
    medianCents: median(amounts),
    amountShape: 'variable',
    minCents: Math.min(...amounts),
    maxCents: Math.max(...amounts),
    monthlyEquivalentCents: pinnedMonthlyCents(txns, key),
    occurrences: collapsed.length,
    firstDate: dates[0],
    lastDate,
    // Projected forward from the last real charge, not the charge date
    // itself — the raw lastDate is a placeholder, not a forecast, and
    // rendering it as "next" would show a due date weeks in the past.
    nextExpected: addDays(lastDate, CADENCE_DAYS.monthly),
    isStale: isSeriesStale(lastDate, 'monthly', asOf),
    detectedBy: 'day-of-month',
  };
}

/**
 * Every recurringKey the ledger could possibly produce: every key detection
 * found, plus every key any spendable transaction would reduce to. A
 * merchant the user pinned or dismissed by hand may be one detection itself
 * never surfaced, so a caller that only walked `detected` would never learn
 * that key exists at all.
 */
export function allRecurringKeys(txns: Txn[], detected: RecurringSeries[]): Set<string> {
  const keys = new Set<string>((detected ?? []).map(s => s.key));
  spendableTxns(txns ?? []).forEach(t => {
    if (t.amount === 0) return;
    const direction: Direction = t.amount < 0 ? 'expense' : 'income';
    keys.add(recurringKey(t.matchKey, direction, t.description));
  });
  return keys;
}

/**
 * Rebuilds the raw-key-to-RecurringState map the pure helpers in this file
 * expect, from the hash-keyed map RTDB actually stores at
 * `recurring-state/{hash64(seriesKey)}` (hashed because a raw series key is
 * not an RTDB-safe path). For every key `allRecurringKeys` can produce, this
 * looks up `hash64(key)` directly AND probes `hash64(\`${key}#${day}\`)` for
 * every billing day 1..31 — bounded by construction, see splitByBillingDay.
 *
 * That probe matters because `allRecurringKeys` (and `recurringKey`, which it
 * calls) never produces a split key: `normalizeStem` strips '#'. So a split
 * key like `expense:APPLE SERVICES#11` is never looked up if a caller only
 * forward-hashes what `allRecurringKeys` returns — the exact bug this
 * replaces. Once detection stops emitting that key (one late charge breaks
 * the cluster), it is in neither `detected` nor anything `recurringKey` can
 * produce, so a pinned or dismissed decision under its hash becomes
 * unreachable: hash64 is one-way, there is no path back from the stored hash
 * to the raw key that produced it. Probing every possible billing day is the
 * only way to find it again.
 */
export function stateForLedger(
  txns: Txn[], detected: RecurringSeries[], byHash: Record<string, RecurringState>,
): Record<string, RecurringState> {
  const state: Record<string, RecurringState> = {};
  allRecurringKeys(txns, detected).forEach(key => {
    const stored = byHash[hash64(key)];
    if (stored) state[key] = stored;
    for (let day = 1; day <= MAX_BILLING_DAY; day++) {
      const splitKey = `${key}#${day}`;
      const splitStored = byHash[hash64(splitKey)];
      if (splitStored) state[splitKey] = splitStored;
    }
  });
  return state;
}

/**
 * Detected series plus a synthesized series for every key the user pinned by
 * hand that detection did not already find — the single source of truth for
 * "everything that counts as a recurring series," shared by the Recurring
 * screen and the dashboard so they can never again compute two different
 * answers for the same ledger (see the Fixed/mo divergence this replaced: the
 * dashboard summed only `detected` and silently dropped a pinned series like
 * a hand-pinned electricity bill that the user pinned specifically so it
 * would count).
 *
 * A key that is both detected and pinned keeps its detected series only —
 * pinning an already-detected series is a no-op for the series itself (see
 * recurring.component.ts's confirm/pin flow), so synthesizing a second copy
 * here would double-count it.
 *
 * Sorted the same way detectRecurring sorts, biggest monthly cost first, so
 * a caller that slices to a top-N (the dashboard's Recurring panel) never
 * silently favors detected series over pinned ones just because of the
 * order they were concatenated in.
 */
export function allRecurringSeries(
  txns: Txn[], detected: RecurringSeries[], state: Record<string, RecurringState>, asOf: string,
): RecurringSeries[] {
  const detectedKeys = new Set((detected ?? []).map(s => s.key));
  // Union in every key the user has ever acted on (Object.keys(state)), not
  // just what allRecurringKeys derives from detection and live transactions.
  // allRecurringKeys never produces a split key (`recurringKey` can't), so a
  // pinned split series that later stops clustering — one late charge breaks
  // it — would otherwise be in neither set and silently drop off the
  // dashboard despite still being pinned. pinnedSeries below still returns
  // null for a stale pinned key with no matching rows, so this can't
  // resurrect a truly empty series.
  const candidateKeys = new Set([...allRecurringKeys(txns, detected), ...Object.keys(state)]);
  const manualExtra = [...candidateKeys]
    .filter(key => !detectedKeys.has(key) && isPinned(key, state))
    // A parent whose billing-day children were detected is already fully
    // represented by those children. Synthesizing it as well would count
    // every one of its transactions a second time.
    .filter(key => !hasSplitChild(key, detected ?? []))
    .map(key => pinnedSeries(txns, key, asOf))
    .filter((s): s is RecurringSeries => s !== null);

  return [...(detected ?? []), ...manualExtra]
    .sort((a, b) => b.monthlyEquivalentCents - a.monthlyEquivalentCents);
}

/** One row of the "Dismissed" section — reversible, whichever kind it started as. */
export interface DismissedEntry {
  key: string;
  merchant: string;
  monthlyCents: number;
  /** True for a dismissed detected series; false for a dismissed "Not detected" candidate. */
  wasDetected: boolean;
}

/**
 * Every dismissed key the user can restore. A dismissed candidate — a key
 * with transactions but no RecurringSeries behind it, since detection never
 * found it — has no detected series to fall back on, so surfacing detected
 * series alone would let it vanish with no route back, quietly recreating
 * the one-way door Task 3 removed. This surfaces both kinds: a dismissed
 * detected series (wasDetected: true, figures straight off the series) and
 * a dismissed candidate (wasDetected: false, name and monthly figure derived
 * from its own transactions via pinnedMonthlyCents — the same math a pinned
 * candidate is costed with, so the two never disagree). A dismissed key
 * with no transactions left behind it at all has nothing to show or
 * restore, so it drops out.
 */
export function dismissedEntries(
  series: RecurringSeries[], txns: Txn[], state: Record<string, RecurringState>,
): DismissedEntry[] {
  const detectedByKey = new Map((series ?? []).map(s => [s.key, s]));

  return Object.keys(state)
    .filter(key => state[key]?.status === 'dismissed')
    .map((key): DismissedEntry | null => {
      const detected = detectedByKey.get(key);
      if (detected) {
        return {
          key, merchant: detected.merchant,
          monthlyCents: detected.monthlyEquivalentCents, wasDetected: true,
        };
      }
      // A parent dismissed before the ledger grew past the too-frequent
      // threshold can later have its children split out by detection (see
      // hasSplitChild). Without this guard it would render here at the
      // merchant's full combined cost while both children are also live and
      // counted in the nut — the same double-count recurringCandidates and
      // allRecurringSeries already guard against.
      if (hasSplitChild(key, series ?? [])) return null;
      const rows = rowsForKey(txns ?? [], key);
      if (!rows.length) return null;
      return {
        key,
        merchant: merchantForKey(key),
        monthlyCents: pinnedMonthlyCents(txns, key),
        wasDetected: false,
      };
    })
    .filter((e): e is DismissedEntry => e !== null);
}

export interface CandidateSeries {
  key: string;
  merchant: string;
  occurrences: number;
  /** Distinct calendar months carrying a charge (not the full span — see calendarMonthsSpanned). */
  months: number;
  medianCents: number;
  lastDate: string;
  /** Short human string explaining why detection missed this merchant. */
  reason: 'day varies' | 'long gap' | 'too few';
}

/**
 * Merchants that look bill-shaped but failed every detection gate — the
 * "Not detected" list on the Recurring screen. Reuses recurringKey,
 * dayOfMonthSigma, occurrencesPerMonth and hasNoLongGap from the detector so
 * this can never silently disagree with detectRecurring about what a bill
 * looks like; only the final gate (folding this shape into a hard yes/no)
 * differs, because here a miss is surfaced rather than discarded.
 */
export function recurringCandidates(
  txns: Txn[], detected: RecurringSeries[], state: Record<string, RecurringState>,
): CandidateSeries[] {
  const detectedKeys = new Set((detected ?? []).map(s => s.key));
  const groups = new Map<string, Txn[]>();

  spendableTxns(txns ?? []).forEach(t => {
    if (t.amount === 0) return;
    const direction: Direction = t.amount < 0 ? 'expense' : 'income';
    const key = recurringKey(t.matchKey, direction, t.description);
    const bucket = groups.get(key);
    if (bucket) bucket.push(t); else groups.set(key, [t]);
  });

  const ranked: { candidate: CandidateSeries; total: number }[] = [];

  groups.forEach((rows, key) => {
    if (detectedKeys.has(key)) return;
    // Load-bearing: this is the only thing keeping a split parent from ever
    // being offered here. If a parent were pinned/dismissed via this list,
    // dismissedEntries/allRecurringSeries would resolve it back to ALL of
    // the merchant's rows via rowsForKey (parent === key, no day filter) —
    // double-counting or contradicting the child series, which still render
    // as their own live detected series.
    if (hasSplitChild(key, detected ?? [])) return;
    if (isPinned(key, state)) return;
    if (state[key]?.status === 'dismissed') return;

    const collapsed = collapseSameDay(rows);
    if (collapsed.length < MIN_DOM_OCCURRENCES) return;

    const dates = collapsed.map(r => r.date);
    const monthCount = new Set(dates.map(d => d.slice(0, 7))).size;
    if (monthCount < MIN_DOM_OCCURRENCES) return;
    if (occurrencesPerMonth(dates) > MAX_PER_MONTH) return;

    const amounts = collapsed.map(r => Math.abs(r.amount));

    const reason: CandidateSeries['reason'] = dayOfMonthSigma(dates) > DOM_SIGMA_MAX
      ? 'day varies'
      : !hasNoLongGap(dates) ? 'long gap' : 'too few';

    ranked.push({
      candidate: {
        key,
        merchant: merchantForKey(key),
        occurrences: collapsed.length,
        months: monthCount,
        medianCents: median(amounts),
        lastDate: dates[dates.length - 1],
        reason,
      },
      total: amounts.reduce((a, b) => a + b, 0),
    });
  });

  return ranked.sort((a, b) => b.total - a.total).map(r => r.candidate);
}
